<template>
    <section class="bg-sectionwize covidproof section-30 generic" id="featured-franchises">
        <div class="container">
            <div class="row justify-content-center">
                <div class="col-md-12">
                    <div class="section-ptb text-center">
                        <h2 class="trending-title mb-3">Featured Franchises</h2>
                    </div>
                </div>
            </div>
            <div class="row justify-content-center">
                <div class="col-md-12">
                    <div v-if="franchiseStore.loading" class="text-center py-5">
                        <div class="loading-spinner">
                            <div class="spinner"></div>
                            <p class="loading-text">Loading franchises...</p>
                        </div>
                    </div>
                    <div v-else-if="franchiseStore.error" class="text-center py-5">
                        <div class="error-container">
                            <i class="fa-solid fa-exclamation-triangle error-icon"></i>
                            <h4 class="error-title">Unable to load franchises</h4>
                            <p class="error-message">{{ franchiseStore.error }}</p>
                            <button @click="loadFranchises" class="retry-btn">
                                <i class="fa-solid fa-refresh"></i>
                                Try Again
                            </button>
                        </div>
                    </div>
                    <div v-else-if="mappedFranchises.length === 0" class="text-center py-5">
                        <div class="no-franchises-container">
                            <i class="fa-solid fa-store-slash no-franchises-icon"></i>
                            <h4 class="no-franchises-title">No featured franchises found</h4>
                            <p class="no-franchises-message">Check back soon for new franchise opportunities.</p>
                        </div>
                    </div>
                    <div v-else class="trending-franchise-wrap d-flex flex-wrap justify-content-center">
                        <CardSection 
                            v-for="franchise in mappedFranchises" 
                            :key="franchise.id"
                            :franchise="franchise" 
                            :show-lead-modal="true"
                            class="m-2" 
                        />
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup>
import { computed, onMounted } from 'vue'
import { useFranchiseStore } from '@/stores/franchiseStore'
import { mapFranchiseData } from '@/utils/formatters'
import CardSection from './CardSection.vue'

const franchiseStore = useFranchiseStore()

const loadFranchises = async () => {
    await franchiseStore.fetchFranchises()
}

onMounted(async () => {
    await loadFranchises()
})

const featuredFranchises = computed(() => {
    // Show all featured franchises, or all if none are featured
    return franchiseStore.franchises.filter(f => f.is_featured).length
        ? franchiseStore.franchises.filter(f => f.is_featured)
        : franchiseStore.franchises.slice(0, 8)
})

const mappedFranchises = computed(() => {
    if (!featuredFranchises.value || !Array.isArray(featuredFranchises.value)) {
        return []
    }
    return featuredFranchises.value.map(franchise => mapFranchiseData(franchise))
})
</script>

<style scoped>
.trending-title {
    font-size: 2.8rem;
    font-weight: 800;
    color: #000000;
    margin-bottom: 1rem;
    letter-spacing: -0.5px;
}

/* Loading State */
.loading-spinner {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
}

.spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #f3f4f6;
    border-top: 4px solid var(--primary-orange, #ff6b35);
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

.loading-text {
    font-size: 1.1rem;
    color: #6c757d;
    font-weight: 500;
}

/* Error State */
.error-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    padding: 2rem;
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 2px 12px rgba(44,62,80,0.08);
    max-width: 500px;
    margin: 0 auto;
}

.error-icon {
    font-size: 3rem;
    color: #f59e0b;
}

.error-title {
    font-size: 1.5rem;
    font-weight: 700;
    color: #2c3e50;
    margin: 0;
}

.error-message {
    font-size: 1rem;
    color: #6c757d;
    margin: 0;
    text-align: center;
}

.retry-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    background: var(--primary-orange, #ff6b35);
    color: #fff;
    border: none;
    border-radius: 8px;
    padding: 12px 24px;
    font-weight: 600;
    font-size: 1rem;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 2px 8px rgba(255,107,53,0.3);
}

.retry-btn:hover {
    background: #e55a2b;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(255,107,53,0.4);
}

/* No Franchises State */
.no-franchises-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    padding: 3rem;
    background: #fff;
    border-radius: 16px;
    box-shadow: 0 2px 12px rgba(44,62,80,0.08);
    max-width: 500px;
    margin: 0 auto;
}

.no-franchises-icon {
    font-size: 3.5rem;
    color: #d1d5db;
}

.no-franchises-title {
    font-size: 1.5rem;
    font-weight: 700;
    color: #2c3e50;
    margin: 0;
}

.no-franchises-message {
    font-size: 1rem;
    color: #6c757d;
    margin: 0;
    text-align: center;
    line-height: 1.6;
}

.trending-franchise-wrap {
    gap: 1rem;
}

@media (max-width: 900px) {
    .trending-title {
        font-size: 2.2rem;
    }
    
    .trending-franchise-wrap {
        display: flex;
        flex-wrap: nowrap;
        overflow-x: auto;
        gap: 16px;
        padding-bottom: 8px;
        scrollbar-width: none;
    }
    
    .trending-franchise-wrap::-webkit-scrollbar {
        display: none;
    }
    
    .trending-franchise-wrap > * {
        min-width: 300px;
        max-width: 90vw;
        flex: 0 0 auto;
    }
}

@media (max-width: 600px) {
    .trending-title {
        font-size: 1.8rem;
    }
    
    .error-container,
    .no-franchises-container {
        margin: 0 1rem;
        padding: 1.5rem;
    }
}
</style>